import React, { useState, useEffect, useMemo } from 'react';
import { adminAPI } from '../api/client';
import { CURRENCY } from '../config/constants';
import { Loader, AlertCircle, Search, X } from 'lucide-react';
import OrderLineItemsEditor, {
  emptyLineItem,
  calcOrderTotalFromItems,
  lineItemsToApiPayload,
  parseLineItemFromApi,
  formatLineItemsSummary,
} from './OrderLineItemsEditor';
import { useGlobalSearch } from '../context/SearchContext';
import { matchesGlobalSearch } from '../utils/globalSearch';
import { istToday, formatIstDate } from '../utils/formatIst';
import { formatOrderStatus } from '../utils/formatOrderStatus';
import {
  ORDER_PERIOD_TABS,
  orderDateInPeriod,
  getIstPeriodBounds,
  defaultCustomRange,
} from '../utils/orderPeriod';
import { useAuth } from '../context/AuthContext';
import { PRIVILEGES } from '../config/roles';
import OrderPaymentsModal from './OrderPaymentsModal';
import {
  buildOrderRepOptions,
  getDefaultOrderRepId,
  isRepUser,
  resolveOrderRepId,
  resolveOrderRepName,
} from '../utils/orderDefaults';
import {
  ORDER_SOURCES,
  SALES_CHANNEL_FILTERS,
  ORDER_SOURCE_STYLES,
  SALES_CHANNEL_STYLES,
  normalizeOrderSource,
  resolveOrderSourceLabel,
  resolveSalesChannelLabel,
  orderMatchesSourceFilter,
  orderMatchesChannelFilter,
  summarizeOrdersByDimensions,
} from '../utils/orderSource';

const PAYMENT_STATUS_STYLES = {
  paid: 'bg-green-100 text-green-700',
  partial: 'bg-amber-100 text-amber-700',
  unpaid: 'bg-gray-100 text-gray-600',
};

const ITEM_STATUSES = ['pending', 'confirmed', 'delivered', 'cancelled'];
const safeItemStatus = (s) => (ITEM_STATUSES.includes(String(s || '').toLowerCase()) ? String(s).toLowerCase() : 'pending');

const OrdersManagement = () => {
  const { user, hasPrivilege } = useAuth();
  const canCreateOrder = hasPrivilege(PRIVILEGES.CREATE_ORDER);
  const canEditOrder = hasPrivilege(PRIVILEGES.EDIT_ORDER);
  const canDeleteOrder = hasPrivilege(PRIVILEGES.DELETE_ORDER);
  const [orders, setOrders] = useState([]);
  const [reps, setReps] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [paymentsOrder, setPaymentsOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [repFilter, setRepFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [periodTab, setPeriodTab] = useState('all');
  const defaultRange = defaultCustomRange();
  const [customDateFrom, setCustomDateFrom] = useState(defaultRange.start);
  const [customDateTo, setCustomDateTo] = useState(defaultRange.end);
  const { query: globalSearch } = useGlobalSearch();

  const customRange = useMemo(
    () => ({ start: customDateFrom, end: customDateTo }),
    [customDateFrom, customDateTo]
  );

  const periodCounts = useMemo(() => {
    const counts = Object.fromEntries(ORDER_PERIOD_TABS.map((t) => [t.id, 0]));
    counts.all = orders.length;
    for (const order of orders) {
      for (const tab of ORDER_PERIOD_TABS) {
        if (tab.id === 'all') continue;
        const range = tab.id === 'custom' ? customRange : null;
        if (orderDateInPeriod(order.order_date, tab.id, range)) {
          counts[tab.id] += 1;
        }
      }
    }
    return counts;
  }, [orders, customRange]);

  const orderLinkUrl = `${window.location.origin}/order`;
  const shareOrderLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Place an order', url: orderLinkUrl });
        return;
      }
      await navigator.clipboard.writeText(orderLinkUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      window.prompt('Customer order link:', orderLinkUrl);
    }
  };

  const [formData, setFormData] = useState({
    rep_id: '',
    customer_id: '',
    order_date: istToday(),
    product_details: '',
    status: 'pending',
    items: [emptyLineItem()],
  });

  const defaultRepId = useMemo(() => getDefaultOrderRepId(user, reps), [user, reps]);

  const repOptions = useMemo(() => buildOrderRepOptions(reps, user), [reps, user]);

  const newOrderFormState = () => ({
    rep_id: defaultRepId,
    customer_id: '',
    order_date: istToday(),
    product_details: '',
    status: 'pending',
    items: [emptyLineItem()],
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!showForm || editingId || !defaultRepId) return;
    setFormData((prev) => (
      prev.rep_id ? prev : { ...prev, rep_id: defaultRepId }
    ));
  }, [showForm, editingId, defaultRepId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [repsRes, customersRes, ordersRes] = await Promise.all([
        adminAPI.getOrderReps().catch(() => ({ data: { data: [] } })),
        adminAPI.getAllCustomers(),
        adminAPI.getAllOrders().catch(() => ({ data: { data: [] } })),
      ]);
      setReps(buildOrderRepOptions(repsRes.data?.data || [], user));
      setCustomers(customersRes.data.data || []);
      setOrders(ordersRes.data.data || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const resolveCustomerPhone = (order) =>
    order.customer_phone || customers.find((c) => c.id === order.customer_id)?.phone_no || '';

  const orderSearchValues = (order) => {
    const repName = resolveOrderRepName(order, reps);
    const customerName =
      order.customer_name || customers.find((c) => c.id === order.customer_id)?.name;
    const customerPhone = resolveCustomerPhone(order);
    const itemSummary = order.items?.length
      ? `${order.items.length} item(s)`
      : formatLineItemsSummary(order);
    const status = String(order.status || '').toLowerCase();
    const paymentStatus = String(order.payment_status || 'unpaid').toLowerCase();
    return [
      repName,
      customerName,
      customerPhone,
      itemSummary,
      order.order_amount,
      order.order_date ? formatIstDate(order.order_date) : '',
      formatOrderStatus(status),
      status,
      paymentStatus,
      resolveOrderSourceLabel(order),
      resolveSalesChannelLabel(order),
      order.id,
    ];
  };

  const periodOrders = useMemo(
    () => orders.filter((order) => orderDateInPeriod(order.order_date, periodTab, customRange)),
    [orders, periodTab, customRange]
  );

  const sourceSummary = useMemo(
    () => summarizeOrdersByDimensions(periodOrders),
    [periodOrders]
  );

  const filteredOrders = useMemo(() => {
    return periodOrders.filter((order) => {
      const status = String(order.status || '').toLowerCase();
      if (statusFilter !== 'all' && status !== statusFilter) return false;

      const paymentStatus = String(order.payment_status || 'unpaid').toLowerCase();
      if (paymentFilter !== 'all' && paymentStatus !== paymentFilter) return false;

      if (repFilter !== 'all' && String(order.rep_id) !== String(repFilter)) return false;
      if (!orderMatchesSourceFilter(order, sourceFilter)) return false;
      if (!orderMatchesChannelFilter(order, channelFilter)) return false;

      const searchFields = orderSearchValues(order);
      if (!matchesGlobalSearch(globalSearch, searchFields)) return false;
      if (searchTerm && !matchesGlobalSearch(searchTerm, searchFields)) return false;

      return true;
    });
  }, [
    periodOrders,
    reps,
    customers,
    globalSearch,
    searchTerm,
    statusFilter,
    paymentFilter,
    repFilter,
    sourceFilter,
    channelFilter,
  ]);

  const periodTotals = useMemo(() => {
    const amount = filteredOrders.reduce(
      (sum, o) => sum + (parseFloat(o.order_amount) || 0),
      0
    );
    return { count: filteredOrders.length, amount };
  }, [filteredOrders]);

  const hasActiveFilters =
    periodTab !== 'all' ||
    searchTerm.trim() !== '' ||
    statusFilter !== 'all' ||
    paymentFilter !== 'all' ||
    repFilter !== 'all' ||
    sourceFilter !== 'all' ||
    channelFilter !== 'all';

  const clearFilters = () => {
    setPeriodTab('all');
    const dr = defaultCustomRange();
    setCustomDateFrom(dr.start);
    setCustomDateTo(dr.end);
    setSearchTerm('');
    setStatusFilter('all');
    setPaymentFilter('all');
    setRepFilter('all');
    setSourceFilter('all');
    setChannelFilter('all');
  };

  const activePeriodLabel =
    ORDER_PERIOD_TABS.find((t) => t.id === periodTab)?.label || 'All';
  const periodBounds = getIstPeriodBounds(periodTab, customRange);

  const validateItems = (items) => {
    const apiItems = lineItemsToApiPayload(items);
    if (apiItems.length === 0) {
      return 'Add at least one line item with material and quantity';
    }
    for (let i = 0; i < apiItems.length; i += 1) {
      if (!apiItems[i].quantity || !apiItems[i].unit_price) {
        return `Line item ${i + 1}: quantity and rate are required`;
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customer_id) {
      setError('Customer is required');
      return;
    }

    const repId = resolveOrderRepId(user, formData.rep_id, reps);
    if (!repId) {
      setError('Sales rep is required');
      return;
    }

    const itemError = validateItems(formData.items);
    if (itemError) {
      setError(itemError);
      return;
    }

    try {
      setError(null);
      const apiItems = lineItemsToApiPayload(formData.items);
      const orderAmount = calcOrderTotalFromItems(formData.items);

      const submitData = {
        rep_id: repId,
        customer_id: parseInt(formData.customer_id, 10),
        order_amount: orderAmount,
        order_date: formData.order_date,
        product_details: formData.product_details || null,
        required_date: null,
        boom_pump_amount: null,
        status: formData.status,
        items: apiItems,
      };

      if (editingId) {
        await adminAPI.updateOrder(editingId, submitData);
        setSuccess('Order updated successfully!');
        setEditingId(null);
      } else {
        await adminAPI.createOrder(submitData);
        setSuccess('Order created successfully!');
      }
      resetForm();
      await fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save order');
    }
  };

  const resetForm = () => {
    setFormData(newOrderFormState());
    setEditingId(null);
    setShowForm(false);
  };

  const openNewOrderForm = () => {
    setEditingId(null);
    setFormData(newOrderFormState());
    setShowForm(true);
  };

  const handleEdit = async (order) => {
    setEditingId(order.id);
    let items = order.items;
    if (!items?.length) {
      try {
        const res = await adminAPI.getOrdersByCustomer(order.customer_id);
        const full = (res.data.data || []).find((o) => o.id === order.id);
        items = full?.items;
      } catch {
        items = [];
      }
    }

    setFormData({
      rep_id: order.rep_id,
      customer_id: order.customer_id,
      order_date: order.order_date ? String(order.order_date).slice(0, 10) : istToday(),
      product_details: order.product_details || '',
      status: order.status,
      items: items?.length ? items.map(parseLineItemFromApi) : [emptyLineItem()],
    });
    setShowForm(true);
  };

  const handleDelete = async (orderId) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        setError(null);
        await adminAPI.deleteOrder(orderId);
        setSuccess('Order deleted successfully!');
        await fetchData();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(err.message || 'Failed to delete order');
      }
    }
  };

  const handleUpdateOrderItemStatus = async (orderId, itemId, nextStatus) => {
    try {
      setError(null);
      await adminAPI.updateOrderItem(itemId, { status: nextStatus });
      // Refresh from server to pick up order.status roll-up.
      await fetchData();
      setExpandedOrderId(orderId);
    } catch (err) {
      setError(err.message || 'Failed to update line item');
    }
  };

  const orderTotalPreview = calcOrderTotalFromItems(formData.items);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 flex items-center justify-center min-h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-600" />
          <p className="text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page space-y-6">
      <div className="admin-page-header">
        <div className="admin-page-header-title">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Orders Management</h2>
          <p className="text-gray-600">Create orders with multiple material line items</p>
        </div>
        <div className="admin-page-header-actions">
          <button
            onClick={shareOrderLink}
            className="bg-white border border-blue-200 text-blue-700 px-4 py-2 rounded-lg font-semibold hover:bg-blue-50 transition flex items-center gap-2"
            title="Public link customers can use to place orders themselves"
          >
            🔗 {linkCopied ? 'Link copied!' : 'Customer order link'}
          </button>
          {canCreateOrder && (
            <button
              onClick={() => (showForm ? resetForm() : openNewOrderForm())}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center gap-2"
            >
              {showForm ? '✕ Cancel' : '+ Add Order'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">{success}</div>
      )}

      {showForm && (canCreateOrder || (canEditOrder && editingId)) && (
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-600 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {editingId ? 'Edit Order' : 'Add New Order'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Sales Rep *</label>
                {isRepUser(user) && defaultRepId ? (
                  <>
                    <input
                      type="text"
                      readOnly
                      value={user?.name || repOptions.find((r) => String(r.id) === defaultRepId)?.name || 'You'}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-800"
                    />
                    <input type="hidden" value={defaultRepId} />
                  </>
                ) : (
                <select
                  value={formData.rep_id}
                  onChange={(e) => setFormData({ ...formData, rep_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                  disabled={editingId !== null}
                >
                  <option value="">-- Choose sales rep --</option>
                  {repOptions.map((rep) => (
                    <option key={rep.id} value={String(rep.id)}>
                      {rep.is_direct_order ? `${rep.name} (no rep credit)` : rep.name}
                    </option>
                  ))}
                </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Select Customer *</label>
                <select
                  value={formData.customer_id}
                  onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                >
                  <option value="">-- Choose Customer --</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name || 'Unnamed Customer'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Order Date *</label>
                <input
                  type="date"
                  value={formData.order_date}
                  onChange={(e) => setFormData({ ...formData, order_date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="md:col-span-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-sm font-semibold text-blue-900">
                  Order total: {CURRENCY.SYMBOL}{orderTotalPreview.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  <span className="text-blue-700 font-normal ml-2">(sum of all line items)</span>
                </p>
              </div>
            </div>

            <OrderLineItemsEditor
              items={formData.items}
              onChange={(items) => setFormData({ ...formData, items })}
            />

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Order notes</label>
              <textarea
                value={formData.product_details}
                onChange={(e) => setFormData({ ...formData, product_details: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="Delivery instructions, site access, etc."
                rows="2"
              />
            </div>

            <div className="flex gap-3">
              <button type="submit" className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition">
                {editingId ? 'Update Order' : 'Save Order'}
              </button>
              <button type="button" onClick={resetForm} className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-semibold hover:bg-gray-400 transition">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 space-y-4">
        <div>
          <p className="text-xs font-medium text-gray-600 mb-2">Period (IST, by order date)</p>
          <div className="flex flex-wrap gap-2">
            {ORDER_PERIOD_TABS.map((tab) => {
              const active = periodTab === tab.id;
              const count = periodCounts[tab.id] ?? 0;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setPeriodTab(tab.id)}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border transition ${
                    active
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {tab.label}
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full ${
                      active ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {periodTab === 'custom' && (
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:items-end mt-3 p-3 bg-gray-50 rounded-lg border border-gray-100 min-w-0">
              <div className="w-full sm:flex-1 sm:min-w-0 sm:max-w-[12rem]">
                <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                <input
                  type="date"
                  value={customDateFrom}
                  onChange={(e) => setCustomDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="w-full sm:flex-1 sm:min-w-0 sm:max-w-[12rem]">
                <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                <input
                  type="date"
                  value={customDateTo}
                  onChange={(e) => setCustomDateTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const dr = defaultCustomRange();
                  setCustomDateFrom(dr.start);
                  setCustomDateTo(dr.end);
                }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium h-[42px]"
              >
                Reset to this month
              </button>
            </div>
          )}

          {periodTab !== 'all' && periodBounds && (
            <p className="text-xs text-gray-500 mt-2">
              {activePeriodLabel}: {formatIstDate(periodBounds.start)}
              {periodBounds.start !== periodBounds.end
                ? ` – ${formatIstDate(periodBounds.end)}`
                : ''}
              {' · '}
              {periodTotals.count} order{periodTotals.count === 1 ? '' : 's'},{' '}
              {CURRENCY.SYMBOL}
              {periodTotals.amount.toLocaleString('en-IN')}
            </p>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mt-4">
            {ORDER_SOURCES.filter((s) => s.id !== 'all').map((tab) => {
              const stats = sourceSummary.bySource[tab.id] || { order_count: 0, revenue_inr: 0 };
              const active = sourceFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setSourceFilter(active ? 'all' : tab.id)}
                  className={`text-left rounded-lg border px-3 py-2 transition ${
                    active
                      ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-200'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{tab.label}</p>
                  <p className="text-lg font-bold text-gray-900">{stats.order_count}</p>
                  <p className="text-xs text-gray-600">{CURRENCY.SYMBOL}{stats.revenue_inr.toLocaleString('en-IN')}</p>
                </button>
              );
            })}
            {SALES_CHANNEL_FILTERS.filter((s) => s.id !== 'all').map((tab) => {
              const stats = sourceSummary.bySalesChannel[tab.id] || { order_count: 0, revenue_inr: 0 };
              const active = channelFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setChannelFilter(active ? 'all' : tab.id)}
                  className={`text-left rounded-lg border px-3 py-2 transition ${
                    active
                      ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-200'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{tab.label}</p>
                  <p className="text-lg font-bold text-gray-900">{stats.order_count}</p>
                  <p className="text-xs text-gray-600">{CURRENCY.SYMBOL}{stats.revenue_inr.toLocaleString('en-IN')}</p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="admin-toolbar border-t border-gray-100 pt-4">
          <div className="admin-toolbar-field">
            <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rep, customer, mobile, status, amount…"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="admin-toolbar-field">
            <label className="block text-xs font-medium text-gray-600 mb-1">Order status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All statuses</option>
              {ITEM_STATUSES.map((s) => (
                <option key={s} value={s}>{formatOrderStatus(s)}</option>
              ))}
            </select>
          </div>
          <div className="admin-toolbar-field">
            <label className="block text-xs font-medium text-gray-600 mb-1">Payment</label>
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All payments</option>
              <option value="paid">Paid</option>
              <option value="partial">Partial</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </div>
          {!isRepUser(user) && (
            <div className="admin-toolbar-field">
              <label className="block text-xs font-medium text-gray-600 mb-1">Sales rep</label>
              <select
                value={repFilter}
                onChange={(e) => setRepFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All reps</option>
                {repOptions.map((rep) => (
                  <option key={rep.id} value={String(rep.id)}>{rep.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="admin-toolbar-field">
            <label className="block text-xs font-medium text-gray-600 mb-1">Order source</label>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {ORDER_SOURCES.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="admin-toolbar-field">
            <label className="block text-xs font-medium text-gray-600 mb-1">Sales type</label>
            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {SALES_CHANNEL_FILTERS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition h-[42px]"
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500">
          Showing {filteredOrders.length} of {orders.length} order{orders.length === 1 ? '' : 's'}
          {periodTab !== 'all' ? ` (${activePeriodLabel.toLowerCase()})` : ''}
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 admin-table-shell">
        <div className="admin-table-scroll">
          <table className="admin-data-table-xl">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-3 w-10" />
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Rep</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Source</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Mobile</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Items</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Payment</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="11" className="px-6 py-4 text-center text-gray-600">
                    {orders.length === 0
                      ? 'No orders found. Add one to get started.'
                      : 'No orders match your filters.'}
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <React.Fragment key={order.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-3 py-4 text-center">
                        <button
                          type="button"
                          onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)}
                          className="text-gray-600 hover:text-gray-900 p-1"
                          title="View line items"
                        >
                          {expandedOrderId === order.id ? '▼' : '▶'}
                        </button>
                      </td>
                      <td className="px-6 py-4 font-semibold text-gray-900">
                        {resolveOrderRepName(order, reps)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1 items-start">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${ORDER_SOURCE_STYLES[normalizeOrderSource(order.order_source)] || 'bg-gray-100 text-gray-700'}`}>
                            {resolveOrderSourceLabel(order)}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${SALES_CHANNEL_STYLES[order.sales_channel || (order.is_direct_sale ? 'direct' : 'rep')] || 'bg-gray-100 text-gray-700'}`}>
                            {resolveSalesChannelLabel(order)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-700">
                        {order.customer_name || customers.find((c) => c.id === order.customer_id)?.name || '-'}
                      </td>
                      <td className="px-6 py-4 text-gray-700 whitespace-nowrap">
                        {resolveCustomerPhone(order) || '-'}
                      </td>
                      <td className="px-6 py-4 text-gray-700 text-sm max-w-xs">
                        {order.items?.length
                          ? `${order.items.length} item(s)`
                          : formatLineItemsSummary(order)}
                      </td>
                      <td className="px-6 py-4 font-semibold text-gray-900">
                        {CURRENCY.SYMBOL}{parseFloat(order.order_amount || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col items-start gap-1">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${PAYMENT_STATUS_STYLES[order.payment_status] || 'bg-gray-100 text-gray-600'}`}>
                            {order.payment_status || 'unpaid'}
                          </span>
                          {parseFloat(order.balance || 0) > 0 && (
                            <span className="text-xs text-gray-500">
                              Bal {CURRENCY.SYMBOL}{parseFloat(order.balance || 0).toLocaleString('en-IN')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-700 text-sm">
                        {order.order_date ? formatIstDate(order.order_date) : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          order.status === 'delivered' ? 'bg-green-100 text-green-700'
                            : order.status === 'cancelled' ? 'bg-red-100 text-red-700'
                            : order.status === 'confirmed' ? 'bg-blue-100 text-blue-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {formatOrderStatus(order.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button type="button" onClick={() => setPaymentsOrder(order)} className="text-emerald-600 hover:text-emerald-700 font-semibold text-sm hover:underline">
                          Payments
                        </button>
                        {canEditOrder && (
                          <button type="button" onClick={() => handleEdit(order)} className="text-blue-600 hover:text-blue-700 font-semibold text-sm hover:underline ml-3">
                            Edit
                          </button>
                        )}
                        {canDeleteOrder && (
                          <button type="button" onClick={() => handleDelete(order.id)} className="text-red-600 hover:text-red-700 font-semibold text-sm hover:underline ml-3">
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedOrderId === order.id && (
                      <tr className="bg-gray-50">
                        <td colSpan="11" className="px-6 py-4">
                          {order.items?.length ? (
                            <div className="space-y-3">
                              <div className="text-xs text-gray-600">
                                Line items: {order.items.length}. Overall status auto-updates when all line items match; otherwise order is Pending.
                              </div>
                              <div className="admin-table-scroll">
                                <table className="min-w-full text-sm">
                                  <thead>
                                    <tr className="text-xs text-gray-600">
                                      <th className="text-left font-semibold py-2 pr-3">Item</th>
                                      <th className="text-left font-semibold py-2 pr-3">Qty</th>
                                      <th className="text-left font-semibold py-2 pr-3">Rate</th>
                                      <th className="text-left font-semibold py-2 pr-3">Boom</th>
                                      <th className="text-left font-semibold py-2 pr-3">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {order.items.map((item) => {
                                      const current = safeItemStatus(item.status);
                                      return (
                                        <tr key={item.id} className="text-gray-800">
                                          <td className="py-2 pr-3 whitespace-nowrap">
                                            {item.product_name}
                                          </td>
                                          <td className="py-2 pr-3 whitespace-nowrap">
                                            {parseFloat(item.quantity)} {item.volume_unit || 'm3'}
                                          </td>
                                          <td className="py-2 pr-3 whitespace-nowrap">
                                            {CURRENCY.SYMBOL}{parseFloat(item.unit_price).toLocaleString('en-IN')}
                                          </td>
                                          <td className="py-2 pr-3 whitespace-nowrap">
                                            {item.boom_pump_amount
                                              ? `${CURRENCY.SYMBOL}${parseFloat(item.boom_pump_amount).toLocaleString('en-IN')}`
                                              : '—'}
                                          </td>
                                          <td className="py-2 pr-3 whitespace-nowrap">
                                            <select
                                              value={current}
                                              onChange={(e) => handleUpdateOrderItemStatus(order.id, item.id, e.target.value)}
                                              className="px-2 py-1 border border-gray-300 rounded-md text-sm bg-white"
                                            >
                                              {ITEM_STATUSES.map((s) => (
                                                <option key={s} value={s}>{formatOrderStatus(s)}</option>
                                              ))}
                                            </select>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-600">{formatLineItemsSummary(order)}</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900 font-medium mb-2">About Orders</p>
        <ul className="text-xs text-blue-800 space-y-1 ml-4 list-disc">
          <li>Each order can include multiple line items (RMC, aggregates, or other products)</li>
          <li>Order total is calculated automatically from all line items</li>
          <li>Click ▶ on a row to expand and view line-item details</li>
          <li>Use <strong>Payments</strong> to record partial payments and view all receipts for an order</li>
        </ul>
      </div>

      {paymentsOrder && (
        <OrderPaymentsModal
          order={paymentsOrder}
          canDelete={canDeleteOrder}
          onClose={() => setPaymentsOrder(null)}
          onChanged={fetchData}
        />
      )}
    </div>
  );
};

export default OrdersManagement;
